import React, { useEffect, useState, useCallback, useMemo } from "react";
import SearchGroupCard from "@components/molecules/SearchGroupCard";
import { useTheme } from "styled-components/native";
import { Container, Spacer } from "@components/atoms";
import { ScrollView } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useNavigation } from "@react-navigation/native";
import { useDispatch } from "react-redux";
import {
  saveGroup,
  useSearchGroupSlice,
} from "@redux/slices/searchGroupsSlice";
import { useTranslation } from "react-i18next";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { getHeaderTitle } from "../utils";
import styled from "styled-components/native";
import { css } from "styled-components/native";
import { useQueryClient } from "@tanstack/react-query";
import LayoutContainer from "@components/layout/LayoutContainer";
import { useUserAnalytics } from "../../../../hooks/useAnalytics";

const ProductSearchGroupScreen = (props: any) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const searchGroupsData = theme.isLargeScreen
    ? props?.searchGroupsData
    : props?.route.params.searchGroupsData;
  const productType = theme.isLargeScreen
    ? props?.productType
    : props?.route.params.productType;
  const navigation = useNavigation();
  const { selectedGroups } = useSearchGroupSlice();
  const queryClient = useQueryClient();

  const [selectedCard, setSelectedCard] = useState("");
  const [expandedView, setExpandedView] = useState(false);
  const { trackWebEvent } = useUserAnalytics();

  const otherSelected = useMemo(
    () =>
      userInfo.user.feature_settings
        ?.move_vdb_public_group_at_last_in_search_group_sequence ||
      !userInfo.user.vdb_setting,
    [userInfo]
  );

  const handleAllVDBPress = useCallback(() => {
    const newGroups = { ...selectedGroups, [productType]: [0] };
    trackWebEvent("select_vdb_search_group", {
      product_type: productType ?? "",
      previous_group: JSON.stringify(selectedGroups) ?? "",
      new_group: JSON.stringify(newGroups) ?? "",
    });
    queryClient.invalidateQueries({ queryKey: ["SEARCH_RESULTS"] });
    dispatch(saveGroup(newGroups));
  }, [selectedGroups, productType]);

  const handleOtherGroupsPress = useCallback(() => {
    if (searchGroupsData?.length > 0) {
      const groupIds = searchGroupsData.map((group: any) => group.id);
      const newGroups = { ...selectedGroups, [productType]: groupIds };
      trackWebEvent("select_other_search_group", {
        product_type: productType ?? "",
        previous_group: JSON.stringify(selectedGroups) ?? "",
        new_group: JSON.stringify(newGroups) ?? "",
      });
      queryClient.invalidateQueries({ queryKey: ["SEARCH_RESULTS"] });
      dispatch(saveGroup(newGroups));
    }
  }, [searchGroupsData, selectedGroups, productType]);

  useEffect(() => {
    navigation.setOptions({
      title: getHeaderTitle(productType, t),
    });
  }, [productType]);

  useEffect(() => {
    const productGroups = selectedGroups[productType];
    if (productGroups && productGroups.length === 1 && productGroups[0] === 0) {
      setSelectedCard("allVDB");
      setExpandedView(false);
    } else if (
      productGroups &&
      (productGroups.length > 1 || productGroups[0] !== 0)
    ) {
      setSelectedCard("otherGroups");
      setExpandedView(true);
    }
  }, [selectedGroups, productType]);

  useEffect(() => {
    if (
      otherSelected &&
      selectedGroups[productType]?.length === 0 &&
      searchGroupsData?.length > 0
    ) {
      const groupIds = searchGroupsData.map((group: any) => group.id);
      dispatch(saveGroup({ ...selectedGroups, [productType]: groupIds }));
      setSelectedCard("otherGroups");
      setExpandedView(true);
    }
  }, [otherSelected, selectedGroups, productType, searchGroupsData]);

  return (
    <LayoutContainer>
      <ScrollView>
        <OverLay>
          <Container>
            <Spacer size={SpaceType.Big} />
            {userInfo.user.vdb_setting && (
              <MemoizedSearchGroupCard
                title={t("searchGroup.card.allVDB.title.text")}
                description={t("searchGroup.card.allVDB.discription.text")}
                checked={selectedCard === "allVDB"}
                onPress={handleAllVDBPress}
                labelColor={theme["core-06"]}
              />
            )}
            {searchGroupsData?.some(
              (group) => Object.keys(group).length > 0
            ) && (
              <MemoizedSearchGroupCard
                title={t("searchGroup.card.other.title.text")}
                description={t("searchGroup.card.other.discription.text")}
                checked={selectedCard === "otherGroups"}
                onPress={handleOtherGroupsPress}
                labelColor={theme["core-06"]}
                expandOtherGroups={expandedView}
                groupData={searchGroupsData}
                productType={productType}
              />
            )}
          </Container>
        </OverLay>
      </ScrollView>
    </LayoutContainer>
  );
};

const MemoizedSearchGroupCard = React.memo(SearchGroupCard);

const OverLay = styled.View`
  ${({ theme }) =>
    (theme.isTablet || theme.isLargeScreen) &&
    css`
      margin-top: 0px;
    `}
`;

export default ProductSearchGroupScreen;
